// Products (AdminProducts.dc.html): every paid product, then the free items, then Who sees what,
// Product types and Old addresses. Owner and admin (the shell). The board draws the Accelerator and
// POYT as moved in; on live in phase 1 they haven't, and their cards say where they live instead.
import { useMemo, useState } from 'react'
import { PageHeader, ButtonLink, Flash, Loading, Problem, useLoad, useAdmin, adminPath } from '../ui/index.js'
import { useNow } from '../../lib/clock.js'
import { PRODUCTS, RULES_H2, RULES, TYPES_H2, TYPES, MOVES_H2, MOVES_LINE, MOVE_STATE } from '../products/copy.js'
import { MOVES, nowLine, freeLine, pricesLine } from '../products/model.js'
import { loadProductsPage, loadAmounts, loadCovers, duplicateProduct } from '../products/data.js'
import { Mark, SectionHead } from '../products/bits.jsx'
import { ProductCard, FreeCard } from '../products/ProductCard.jsx'
import { SeeAsDialog } from '../products/SeeAsDialog.jsx'
import '../../styles/admin-products.css'

export default function Products({ api, me }) {
  const { reloadProducts } = useAdmin()
  const nowMs = useNow(60000)
  const page = useLoad(() => loadProductsPage(api, Date.now()), [api])
  const products = page.data?.products || []
  // Stripe's amounts and the cover photos come after the page, so it draws without waiting for them
  const idsKey = products.map(p => p.id).join(',')
  const coversKey = products.filter(p => p.cover_path).map(p => `${p.id}:${p.cover_path}`).join(',')
  const amounts = useLoad(() => (page.data ? loadAmounts(api, products, page.data.map) : null), [idsKey, page.data?.map])
  const covers = useLoad(() => (page.data ? loadCovers(api, products).catch(() => ({})) : null), [coversKey, Boolean(page.data)])
  const [open, setOpen] = useState(null) // { id, panel }
  const [seeAs, setSeeAs] = useState(null)
  const [flash, setFlash] = useState(null)
  const [duplicating, setDuplicating] = useState(null)

  const data = useMemo(() => {
    if (!page.data) return null
    const d = { ...page.data, products }
    d.nowLines = Object.fromEntries(products.map(p => [p.id, nowLine(p, d, nowMs)]))
    d.freeLines = Object.fromEntries(products.map(p => [p.id, freeLine(p, d, nowMs)]))
    return d
  }, [page.data, products, nowMs])

  const change = (id, patch) => page.setData(d => ({ ...d, products: d.products.map(p => (p.id === id ? { ...p, ...patch } : p)) }))
  const checklist = (id, items) => page.setData(d => ({ ...d, checklist: [...d.checklist.filter(i => i.product_id !== id), ...items] }))

  async function duplicate(p) {
    if (duplicating) return
    setDuplicating(p.id)
    setFlash(null)
    try {
      const { partial, weeks, modules } = await duplicateProduct(api, p, products, me?.who?.person_id)
      setFlash({ text: partial ? PRODUCTS.copiedPart(p.name) : PRODUCTS.copied(p.name, { weeks, modules }) })
      await page.reload()
      reloadProducts?.()
    } catch (e) {
      setFlash({ text: e.message, problem: true })
    } finally {
      setDuplicating(null)
    }
  }

  const amountsFor = amounts.status === 'error' ? 'error' : amounts.data
  const paid = products.filter(p => p.access_kind === 'paid')
  const free = products.filter(p => p.access_kind === 'free')
  // See it as a member on a private client space: only that product's clients (each product's spaces
  // stay apart)
  const clientsOf = product => (data ? (data.spaces || []).filter(s => s.product_id === product?.id)
    .map(s => (data.people || []).find(x => x.id === s.client_person_id)).filter(Boolean) : [])

  return (
    <div className="ap-page">
      <PageHeader eyebrow={PRODUCTS.eyebrow} title={PRODUCTS.title}
        actions={<ButtonLink to={adminPath.addProduct()} kind="ink"><Mark name="plus" size={15} className="ap-mark ap-mark--bold" />{PRODUCTS.add}</ButtonLink>} />
      {flash && <Flash tone={flash.problem ? 'problem' : 'done'} onClose={() => setFlash(null)} closeLabel={PRODUCTS.close}>{flash.text}</Flash>}

      {page.status === 'loading' && <Loading />}
      {page.status === 'error' && <Problem error={page.error} onRetry={page.reload} />}
      {data && (
        <>
          <SectionHead id="ap-paid" title={PRODUCTS.paidH2} aside={PRODUCTS.paidAside} />
          <section className="ap-list" aria-labelledby="ap-paid">
            {paid.length === 0 && <p className="ad-empty ap-list-empty">{PRODUCTS.empty}</p>}
            {paid.map(p => (
              <ProductCard key={p.id} api={api} product={p} data={data} cover={covers.data?.[p.id]}
                prices={pricesLine(p, data.map, amountsFor)}
                open={open?.id === p.id ? open.panel : null} onOpen={panel => setOpen(panel ? { id: p.id, panel } : null)}
                onChange={patch => change(p.id, patch)} onChecklist={items => checklist(p.id, items)}
                onDuplicate={() => duplicate(p)} duplicating={duplicating === p.id} onSeeAs={() => setSeeAs(p)} />
            ))}
          </section>

          <SectionHead id="ap-free" title={PRODUCTS.freeH2} aside={PRODUCTS.freeAside} />
          <section className="ap-list" aria-labelledby="ap-free">
            {free.length === 0 && <p className="ad-empty ap-list-empty">{PRODUCTS.freeEmpty}</p>}
            {free.map(p => (
              <FreeCard key={p.id} api={api} product={p} data={data} cover={covers.data?.[p.id]} onChange={patch => change(p.id, patch)}
                onDuplicate={() => duplicate(p)} duplicating={duplicating === p.id} />
            ))}
          </section>
        </>
      )}

      <div className="ap-panels">
        <section className="ad-card ap-info" aria-labelledby="ap-rules">
          <h2 className="ap-info-h" id="ap-rules">{RULES_H2}</h2>
          <ol className="ap-rules-list">
            {RULES.map((r, i) => (
              <li key={r.head} className="ap-rule-row">
                <span className="ap-num" aria-hidden="true">{i + 1}</span>
                <span className="ap-rule-text"><span className="ap-rule-head">{r.head}</span><span className="ap-rule-body">{r.body}</span></span>
              </li>
            ))}
          </ol>
        </section>
        <section className="ad-card ap-info" aria-labelledby="ap-types">
          <h2 className="ap-info-h" id="ap-types">{TYPES_H2}</h2>
          <dl className="ap-types">
            {TYPES.map(t => (
              <div key={t.key} className="ap-type">
                <dt><span className="ap-type-name">{t.name}</span><span className="ap-type-access">{t.access}</span></dt>
                <dd>{t.panel}</dd>
              </div>
            ))}
          </dl>
        </section>
      </div>

      <section className="ad-card ap-info" aria-labelledby="ap-moves">
        <h2 className="ap-info-h" id="ap-moves">{MOVES_H2}</h2>
        <p className="ap-info-line">{MOVES_LINE}</p>
        <ul className="ap-moves">
          {MOVES.map(m => (
            <li key={m.from} className="ap-move">
              <span className="ap-move-from">{m.from}</span>
              <span className="ap-move-to">
                {m.to}
                <span className={`ap-move-state${m.live ? ' ap-move-state--live' : ''}`}>{m.live ? MOVE_STATE.live : `${MOVE_STATE.waiting}. ${m.when}`}</span>
              </span>
            </li>
          ))}
        </ul>
      </section>

      {seeAs && <SeeAsDialog api={api} product={seeAs} clients={clientsOf(seeAs)} onClose={() => setSeeAs(null)} />}
    </div>
  )
}
